import { Injectable } from '@angular/core';
import { CustomBaseStore } from '@city/const';
import { BaseStoreFacade } from '@city/data-access';
import { environment } from '@core/const';
import { WindowRef } from '@spartacus/core';
import { BehaviorSubject, filter, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { finalize, map, take } from 'rxjs/operators';
import { CookieService, EnvironmentService } from '../services';
import { SmartCaptchaInjectorService } from './smart-captcha-injector.service';
import {
  SmartCaptchaEvents,
  SmartCaptchaGlobalObject,
  SmartCaptchaLanguages,
  SmartCaptchaRenderParams,
} from './smart-captcha.model';

@Injectable()
export class SmartCaptchaService {
  private smartCaptchaGlobalObject: SmartCaptchaGlobalObject;
  private widgetId: number;
  private hostElement: HTMLDivElement;

  private queue: Subject<void>[] = [];
  private inProgress = false;

  private isInitializeStarted = false;
  private isInitializeFinished$ = new BehaviorSubject(false);

  constructor(
    private smartCaptchaInjectorService: SmartCaptchaInjectorService,
    private cookieService: CookieService,
    private environmentService: EnvironmentService,
    private windowRef: WindowRef,
    private baseStoreFacade: BaseStoreFacade,
  ) {}

  execute(): Observable<string | undefined> {
    return this.isDisabled$().pipe(
      switchMap((isDisabled: boolean) => {
        if (isDisabled) {
          this.log('status disabled');

          return of(undefined);
        }

        this.log('execute started');

        // Yandex SmartCaptcha не работает с SSR, поэтому используем секретный токен для обхода этого ограничения
        if (this.environmentService.isPlatformServer) {
          return of(environment.smartCaptchaBypassToken);
        }

        if (!this.isInitializeStarted) {
          this.isInitializeStarted = true;

          return this.init$().pipe(switchMap(() => this.continueExecution()));
        }

        return this.continueExecution();
      }),
    );
  }

  private continueExecution(): Observable<string | undefined> {
    if (this.inProgress) {
      this.log('add process to queue');
      const subject = new BehaviorSubject<void>(undefined);
      this.queue.push(subject);

      return subject.pipe(switchMap(() => this.createObservable()));
    }

    this.inProgress = true;

    return this.createObservable();
  }

  private createObservable(): Observable<string> {
    this.log('create observable');

    return this.isInitializeFinished$.pipe(
      filter(Boolean),
      switchMap(() => this.getToken()),
      finalize(() => this.processNext()),
    );
  }

  private processNext(): void {
    if (this.queue.length > 0) {
      this.log('get next process from queue');
      this.queue.shift()?.next();
    } else {
      this.inProgress = false;
    }
  }

  private init$(params: Partial<SmartCaptchaRenderParams> = {}): Observable<void> {
    this.log('intialize started');

    return this.isDisabled$().pipe(
      filter((isDisabled) => !isDisabled),
      switchMap(() => this.smartCaptchaInjectorService.init()),
      tap((data) => {
        this.smartCaptchaGlobalObject = data.globalObject;
        this.hostElement = this.createHostElement();

        this.widgetId = data.globalObject.render(this.hostElement, {
          sitekey: 'ysc1_TpmwGj7pxaBXT7Fqqc3VZhnfxMzi069aolAdfflIc5271f2f',
          hl: SmartCaptchaLanguages.Ru,
          hideShield: true,
          invisible: true,
          test: this.isDebugMode(),
          ...params,
        });

        this.log('intialize finished');
        this.isInitializeFinished$.next(true);
      }),
      map(() => void 0),
    );
  }

  private createHostElement(): HTMLDivElement {
    const el = this.windowRef.document.createElement('div');

    return this.windowRef.document.body.appendChild(el);
  }

  private isDebugMode(): boolean {
    return this.cookieService.get('sd_smart_captcha_debug_on') === '1';
  }

  private isDisabled$(): Observable<boolean> {
    return this.baseStoreFacade.getActiveBaseStore().pipe(
      take(1),
      map((baseStore: CustomBaseStore | undefined) => {
        return this.cookieService.isAutomationTest() || !baseStore?.captchaCheckEnabled;
      }),
    );
  }

  private log(...msgs: string[]): void {
    const row = [new Date().toLocaleTimeString(), 'yandex smart captcha', ...msgs];

    if (this.windowRef.isBrowser()) {
      if (!('__smartCaptchaLog' in (this.windowRef.nativeWindow as any))) {
        Object.defineProperty(this.windowRef.nativeWindow, '__smartCaptchaLog', {
          value: [],
        });
      }

      (this.windowRef.nativeWindow as any).__smartCaptchaLog.push(row);
    }

    if (this.cookieService.get('sd_smart_captcha_debug_on') === '2') {
      // eslint-disable-next-line no-console
      console.log(row.join(' | '));
    }
  }

  private getToken(): Observable<string> {
    return new Observable((subscriber) => {
      this.log('global execute started');
      this.smartCaptchaGlobalObject.execute(this.widgetId);

      const unsubscribe = [
        this.smartCaptchaGlobalObject.subscribe(this.widgetId, SmartCaptchaEvents.Success, (token: string) => {
          this.log(SmartCaptchaEvents.Success);
          subscriber.next(token);
          subscriber.complete();
        }),

        this.smartCaptchaGlobalObject.subscribe(this.widgetId, SmartCaptchaEvents.ChallengeHidden, () => {
          this.log(SmartCaptchaEvents.ChallengeHidden);
          subscriber.error('Проверка не пройдена');
          subscriber.complete();
        }),

        this.smartCaptchaGlobalObject.subscribe(this.widgetId, SmartCaptchaEvents.NetworkError, () => {
          this.log(SmartCaptchaEvents.NetworkError);
          subscriber.error('Не удалось загрузить капчу');
          subscriber.complete();
        }),

        this.smartCaptchaGlobalObject.subscribe(this.widgetId, SmartCaptchaEvents.JavaScriptError, () => {
          this.log(SmartCaptchaEvents.JavaScriptError);
          subscriber.error('Ошибка в скрипте');
          subscriber.complete();
        }),

        this.smartCaptchaGlobalObject.subscribe(this.widgetId, SmartCaptchaEvents.TokenExpired, () => {
          this.log(SmartCaptchaEvents.TokenExpired);
          subscriber.error('Токен протух');
          subscriber.complete();
        }),
      ];

      return (): void => {
        this.log('reset');
        unsubscribe.forEach((cb) => cb());
        this.smartCaptchaGlobalObject.reset(this.widgetId);
        this.inProgress = false;
      };
    });
  }
}
